import React from 'react';
import { Transaction } from 'model';
import { Branding, useStateManager, useUIState, BrandingStyle } from "bcms-react";

import { SVGAsset, ImageAsset } from 'res';
import MobileTableAccordionView, { MobileTableAccordionDataItem, MobileTableAccordionToggle } from 'components/shared/views/MobileTableAccordionView';
import { CustomAccordionClose, CustomAccordionContent } from 'components/custom-ui/CustomAccordion';
import { FormContainer, FormRowView } from 'components/shared';
import "./styles.css";
import { getTransactionAccordionConfig } from 'components/custom-ui/CustomTransactionAccordion';
import NoTransactionsView from '../NoTransactionsView';
import { UIButton } from 'components/ui';

/**
 * @function MobileTransactionsTable
 * @param {DECIMAL} amount - This is the amount of the transaction
 * @param {DECIMAL} balance - This is the running balance of the account after each transaction
 * @param {DOUBLE} id - This is the transaction ID
 * @param {String} description - This is the description of the transaction (what the transaction is)
 * @param {String} type - This is the type of transaction
 * @param {BCMSDate} postedDate - This is the posted date for the transaction
 * @param {BCMSDate} date - This is the transaction date
 * @return {React.Component}
 * @exported true
 * @description The main component for MobileTransactionsTable
 * @example
 * <MobileTransactionsTable amount="10.87" balance="1000" id="214523" description="Gold Key Club x4784" type="Loan Addon" date="10/21/2022" postedDate="10/21/2022" />
 */
export default function MobileTransactionsTable(props) {
  const { data, ui, onHasMoreClick } = useStateManager();

  const account = data.account;
  const accountID = account.id;

  const filtered = data.transactions;
  const nonFiltered = Transaction.initByAccount(accountID);

  const filterTransactions = (transactions, status) => transactions.filter(transaction => {
      return transaction.account ? transaction.status === status : false;
  }, []);

  // Filter transactions by status
  const pendingTransactions = filterTransactions(filtered, "pending");
  let postedTransactions = filterTransactions(filtered, "posted");
  const allTransactions = [ ...pendingTransactions, ...postedTransactions ];

  // Get the balance from the account
  let balance = account.availableBalance ? account.availableBalance.amount : account.currentBalance.amount;
  // Go through the non filtered transactions and filter them by status and set the balance
  const nonFilteredPending = filterTransactions(nonFiltered, "posted").map(transaction => {
      balance = account.type === "loan" ? (balance + transaction.amount.amount) : (balance - transaction.amount.amount);
      transaction.balance = balance;

      return transaction;
  }, []);
  
  // Apply the balance to the filtered transactions
  // Doing this keeps the correct balance even when transactions are filtered
  postedTransactions = postedTransactions.map(transaction => {
      const related = nonFilteredPending.find(item => item.id === transaction.id);
      transaction.balance.amount = related !== undefined && related.balance.amount !== undefined ? related.balance.amount : 0;

      return transaction;
  }, []);

  let hasMore = postedTransactions.length > 0;
  postedTransactions = hasMore && ui.showMoreTransactions === false ? postedTransactions.slice(0, 20) : postedTransactions;

  // Check if all transactions are shown.
  if((allTransactions.length) === (postedTransactions.length + pendingTransactions.length)) {
      hasMore = false;
  }

  const pendingItems = pendingTransactions.map(transaction => {
    return <MobileTransactionsTableItem key={ `Transaction_${ transaction.id }` } transaction={ transaction } />
  }, []);
  const postedItems = postedTransactions.map(transaction => {
    return <MobileTransactionsTableItem key={ `Transaction_${ transaction.id }` } transaction={ transaction } />
  }, []);

  return (
      
      <div className="mobile-transactions-table show-on-mobile">
        {/* Content for transactions. */}
        { allTransactions.length > 0 && 
        <>
        <MobileTransactionsTableStatusView text="Pending"/>
          { pendingItems }

        <MobileTransactionsTableStatusView text="Posted" />
          { postedItems }
      
          { !hasMore && <EndOfTransactionsMessage /> } 
          { hasMore && <div className="text-center show-more-transactions-container">
          <UIButton text="Show More Transactions" className="secondary-button show-more" onClick={ onHasMoreClick } />
          </div>}
        </>
        }
        {/* Content for no transactions. */}
        { allTransactions.length === 0 && 
        <NoTransactionsView/>
        }
      </div>
      
  );
}

function MobileTransactionsTableItem(props) {
  const transaction = props.transaction; 
 
  const uiStateConfig = getTransactionAccordionConfig(transaction.id);
  const { config, key } = useUIState(uiStateConfig.key, uiStateConfig.value);
  const { onItemClick } = useStateManager();

  const toggleClick = value => onItemClick(key, "accordionActive", value);

  const date = transaction.date.format("m/d/Y") ?? "[[MM/DD/YYYY]]";
  const postedDate = transaction.status === "pending" ? "Pending" : date;
  let description = transaction.description  ?? "";
  description = transaction.status === "pending" ? description + " (Pending)" : description;
  const amount = transaction.amount.format() ?? "$X.XX";
  const balance = transaction.balance.amount ? transaction.balance.format() : "";
  
  let type = transaction.type ?? ""; 
  type = `${ type.charAt(0).toUpperCase() }${ type.slice(1) }`;

  const isDeposit = transaction.filterType == "deposit";
  const checkClick = evt => onItemClick(key, "showChecks", !config.showChecks);

  return (
    <MobileTableAccordionView active={ config.accordionActive }>
      <MobileTableAccordionToggle 
        rightValue={
          <div className="flex-container flex-vertical">
            <p className={ isDeposit ? "credit-amount" : "" }>{ amount }</p>
            <p className="light-text running-balance">{ balance }</p>
          </div>
        }
        depositID={ transaction.id }
        onClick={ toggleClick }>
        {/* columnHeader is left empty to hide the light-colored text that shows to the right by default */}
        <MobileTableAccordionDataItem columnHeader="" data={ description } />
        {/* data is left empty to hide the bold text that shows to the left by default */}
        <MobileTableAccordionDataItem columnHeader={ date } data="" />
      </MobileTableAccordionToggle>
      
      <CustomAccordionContent>
        <div className="flex-container custom-accordion-header">
          <p className="flex-width">TRANSACTION DETAILS</p>
          <CustomAccordionClose className="set-width clickable" onClick={ toggleClick } >
            <SVGAsset id="CloseX" className="icon" data-point={`closeX_${ transaction.id }`}/>
          </CustomAccordionClose>
        </div>
        <FormContainer>
          <FormRowView text="Transaction Date" className="light-text">
            <p><b>{ date }</b></p>
          </FormRowView>
          <FormRowView text="Posted Date" className="light-text">
            <p><b>{ postedDate }</b></p>
          </FormRowView>
          <FormRowView text="Type" className="light-text type">
            <p><b>{ type }</b></p>
          </FormRowView>
          { transaction.type === "check" &&
          <Branding id="view_check_images_abi22" value="true">
            <FormRowView>
              <a className="clickable" onClick={ checkClick } data-point={`View_Hide_Check_Link_${ transaction.id }`}>{ config.showChecks ? "Hide" : "View" } Check</a>
            </FormRowView>
          </Branding> 
          }
          { config.showChecks &&
          <>
            <FormRowView>
              <ImageAsset id="CheckFront" data-point={"Front_Check"}/>
            </FormRowView>
            <FormRowView>
              <ImageAsset id="CheckBack"  data-point={"Back_Check"}/>
            </FormRowView>
          </> 
          }
        </FormContainer>
      </CustomAccordionContent>
    </MobileTableAccordionView>
  );
}

/**
 * @function MobileTransactionsTableStatusView
 * @param {String} text - This is the status of the group of transactions
 * @return {React.Component}
 * @exported false
 * @description This is the status divider that shows the transactions groups
 * @example
 * <MobileTransactionsTableStatusView text="Posted" />
 */
function MobileTransactionsTableStatusView(props) {
  const text = props.text ?? "Text";
  return (
    <div className="mobile-transactions-table-status-view">
        <p><b>{text}</b></p>
    </div>

  );
}

/**
 * @function EndOfTransactionsMessage
 * @return {React.Component}
 * @exported false
 * @description This is a message that is to be displayed at the end of the transaction table when there are no more transactions to be displayed
 * @example
 * <EndOfTransactionsMessage />
 */
 function EndOfTransactionsMessage(props){
  return(
    <div className="end-of-transactions-message flex-container">
      <SVGAsset id="InfoIcon" className="info-icon set-width" />
      <p className="flex-width">End of recent transactions. Please use the Search feature to see older transactions.</p>
    </div>
  );
}